import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { ADMIN_MANY_ORDERS_API_PATHS } from '../constants/orders-api-paths.constant';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { Order } from '../types/order.type';

interface OrdersFilters {
  status?: string;
  advStatus?: string;
  deliveryDate?: string;
  organizationId?: string;
  ownerId?: string;
  deliveryId?: string;
  invitationId?: string;
}

interface OrdersResponse {
  orders: Order[];
  totalCount: number;
}

interface OrdersState {
  loading: boolean;
  totalCount: number;
  orders: Order[];
  error: string | null;
}

export interface UseFindOrdersParams {
  page: number;
  limit: number;
  sortDirection: 'asc' | 'desc';
  filters: OrdersFilters;
}

export function useFindOrders({ page, limit, sortDirection, filters }: UseFindOrdersParams) {
  const { enqueueSnackbar } = useSnackbar();

  const [result, setResult] = useState<OrdersState>({
    loading: true,
    totalCount: 0,
    orders: [],
    error: null,
  });

  const fetchOrders = useCallback(() => {
    setResult((prev) => ({ ...prev, loading: true }));

    HttpClient.get<OrdersResponse>(ADMIN_MANY_ORDERS_API_PATHS.GET_ORDERS, {
      params: {
        page,
        limit,
        sortDirection,
        filters: {
          status: filters.status,
          advStatus: filters.advStatus,
          deliveryDate: filters.deliveryDate,
          organizationId: filters.organizationId,
          ownerId: filters.ownerId,
          deliveryId: filters.deliveryId,
          invitationId: filters.invitationId,
        },
      },
    })
      .then((response) => {
        setResult({
          loading: false,
          totalCount: response.totalCount,
          orders: response.orders,
          error: null,
        });
      })
      .catch((error) => {
        const message =
          error.response?.data?.message ?? `Une erreur est survenue lors de la récupération des commandes`;
        enqueueSnackbar(message, { variant: 'error' });
        setResult({
          loading: false,
          totalCount: 0,
          orders: [],
          error: message,
        });
      });
  }, [
    page,
    limit,
    sortDirection,
    filters.status,
    filters.advStatus,
    filters.deliveryDate,
    filters.organizationId,
    filters.ownerId,
    filters.deliveryId,
    filters.invitationId,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { ...result, fetchOrders };
}
